import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'

import L from 'leaflet'
import React, { ComponentProps, useEffect, useState } from 'react'

import { DEFAULT_MAP_CENTER_LAT, DEFAULT_MAP_CENTER_LON, DEFAULT_MAP_ZOOM } from '@/shared/config/consts'
import { styled } from '@mui/material'

import { LeafletMapContext } from './LeafletMapContext'
import { useDrawControls } from './libs/useDrawControls'
import { useLeafletMap } from './libs/useLeafletMap'

export type LeafletMapProps = Pick<ComponentProps<typeof StyledContainer>, 'sx'> & L.MapOptions & {
  children?: React.ReactNode
}

export const LeafletMap: React.FC<LeafletMapProps> = ({ children, sx, zoom, center, ...mapOptions }) => {
  const [mapContainer, setMapContainer] = useState<HTMLDivElement | null>(null)

  const { map } = useLeafletMap({
    node: mapContainer,
    mapStyleOption: 'explore',
    zoom: zoom || DEFAULT_MAP_ZOOM,
    center: center || new L.LatLng(DEFAULT_MAP_CENTER_LAT, DEFAULT_MAP_CENTER_LON),
    ...mapOptions,
  })

  useDrawControls({ map, isDrawControlEnabled: mapOptions.drawControl || false })

  // Resize map when container size changes
  useEffect(() => {
    if (!map || !mapContainer) return
    
    const resizeObserver = new ResizeObserver(() => {
      map?.invalidateSize()
    })
    
    resizeObserver.observe(mapContainer)

    return () => {
      resizeObserver.disconnect()
    }
  }, [map, mapContainer])

  return (
    <LeafletMapContext.Provider value={{ map }}>
      <StyledContainer ref={(node) => setMapContainer(node)}>
        {children}
      </StyledContainer>
    </LeafletMapContext.Provider>
  )
}

const StyledContainer = styled('div')`
  font-family: ${props => props.theme.typography.fontFamily};
  width: 100%;
  height: 100%;
`